<template>
  <div>

    <div class="row d-flex justify-content-center" v-if="hasSubscription && currentSubscriptionStatus === 'past_due'">
      <div class="col-12 col-md-6 text-center">
        <h3 class="fw-bold">{{ 'Subscription' | translate }}</h3>
        <div class="card text-start">
          <div class="card-body">
            <h4>
              {{ 'Status' | translate }}:
              <b class="text-warning">
                {{ 'past_due (payment failed)' | translate }}
                <span v-if="currentSubscription.cancel_at_period_end">
                  ({{ 'canceles at' | translate }}:
                  {{
                    dayjs
                      .unix(currentSubscription.current_period_end)
                      .format('YYYY/MM/DD')
                  }})
                </span>
              </b>
            </h4>
            <div class="mb-4" v-if="card">
              <h4>{{ 'Payment method' | translate }}:</h4>
              <span>
                <b class="text-uppercase">{{ card.brand }}</b>
              </span>
              <span>
                <b>**** **** **** {{ card.last_four }}</b>
              </span>
            </div>
            <div class="mb-4" v-if="nextPaymentAttempt">
              <h4>{{ 'Next attempt' | translate }}: <b>{{ nextPaymentAttempt }}</b></h4>
            </div>
            <div class="mb-4" v-if="paymentMethodHasChanged">
              <span>{{ 'Status change can take up to 72h' | translate }}:</span>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12">
        <StripeCheckoutChangePaymentMethod
          v-if="!paymentMethodHasChanged"
          :stripe-key="stripeKey"
          text-button="Update card"
          @paymentMethodChanged="paymentMethodChanged"
        ></StripeCheckoutChangePaymentMethod>
      </div>
    </div>

    <div class="row d-flex justify-content-center" v-else-if="hasSubscription">
      <div class="col-12 col-md-6 text-center">
        <h3 class="fw-bold">{{ 'Subscription' | translate }}</h3>
        <div class="card text-start">
          <div class="card-body">
            <h4>
              {{ 'Status' | translate }}:
              <b>
                {{ currentSubscriptionStatus }}
                <span v-if="currentSubscription.cancel_at_period_end">
                  ({{ 'canceles at' | translate }}:
                  {{
                    moment
                      .unix(currentSubscription.current_period_end)
                      .format('YYYY/MM/DD')
                  }})
                </span>
              </b>
            </h4>
            <div class="mb-4" v-if="card">
              <h4>{{ 'Payment method' | translate }}:</h4>
              <span>
                <b class="text-uppercase">{{ card.brand }}</b>
              </span>
              <span>
                <b>**** **** **** {{ card.last_four }}</b>
              </span>
            </div>
            <a
              href="#"
              class=""
              @click.prevent="changePaymentMethod = !changePaymentMethod"
            >
              {{ 'Change payment method' | translate }}
            </a>
            <br />
            <a href="#" class="" @click.prevent="handleChangeSubscription">
              {{ 'Change plan' | translate }}
            </a>
            <br />
            <a href="#" class="" @click.prevent="handleCancelSubscription">
              {{ 'Cancel subscription' | translate }}
            </a>
          </div>
        </div>
      </div>
    </div>    


    <div
      v-else-if="!hasSubscription"
      class="row d-flex justify-content-center"
    >
      <div class="col-12 mb-3 text-center">
        {{ 'Before choosing your plan' | translate }},
        <router-link :to="{ name: 'settings.account' }">
          {{ 'you can change currency here' | translate }}
        </router-link>
      </div>

      <div class="col-12 col-md-6">
        <div class="row mb-4">
          <div
            class="col-6"
            v-for="(plan, index) in plans"
            :key="plan.price_id"
          >
            <div
              class="card"
              :class="{
                'border-success': plan.priceId === selected,
              }"
            >
              <div class="card-body">
                <h2 class="text-capitalize text-muted">{{ index }}</h2>
                <h1 class="fw-bold m-0">
                  {{ plan.base }} {{ currency }}
                </h1>
                <p>
                  {{ 'Per month flat fee' | translate }} ({{
                    'net' | translate
                  }})
                </p>

                <h3 class="m-0">+ {{ plan.provision }}%</h3>
                <p>
                  {{ 'Sales provision' | translate }} ({{ 'net' | translate }})
                </p>

                <div class="text-center">
                  <button
                    v-if="plan.priceId === selected"
                    class="btn btn-success"
                  >
                    {{ 'Selected' | translate }}
                  </button>
                  <button
                    v-else
                    class="btn btn-primary"
                    @click="selectPlan(plan)"
                  >
                    {{ 'Select' | translate }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <StripeCheckoutCardElement
      v-if="selected && !hasSubscription"
      :plan="selectedPlan"
      :currency="currency"
      :stripe-key="stripeKey"
      @subscriptionCreated="subscriptionCreated"
    ></StripeCheckoutCardElement>

    <StripeCheckoutChangePaymentMethod
      v-if="changePaymentMethod"
      :stripe-key="stripeKey"
      @paymentMethodChanged="paymentMethodChanged"
    ></StripeCheckoutChangePaymentMethod>

    <div class="row d-flex justify-content-center my-3">
      <div class="col-12 col-md-6 text-center">
        <img
          src="https://djceg34j53tq.cloudfront.net/public/powered_by_stripe.svg"
          alt="Powered by Stripe"
        />
      </div>
    </div>
    <ConfirmActionModal ref="ConfirmAction"></ConfirmActionModal>
  </div>
</template>

<script>
import StripeCheckoutChangePaymentMethod from './StripeCheckoutChangePaymentMethod'
import StripeCheckoutCardElement from './StripeCheckoutCardElement'
import ConfirmActionModal from '~/app2/components/ConfirmActionModal'
import dayjs from 'dayjs'
import _ from 'lodash'

export default {
  components: {
    ConfirmActionModal,
    StripeCheckoutCardElement,
    StripeCheckoutChangePaymentMethod,
  },
  props: ['stripeKey', 'subscriptionsData', 'plans', 'currency', 'card'],
  data() {
    return {
      changePaymentMethod: false,
      paymentMethodHasChanged: false,
      cardHolderName: '',
      stripe: Stripe(this.stripeKey),
      elements: null,
      cardElement: null,
      cardErrors: '',
      selected: null,
      subscriptions: this.subscriptionsData,
    }
  },
  methods: {
    paymentMethodChanged() {
      this.changePaymentMethod = false
      this.paymentMethodHasChanged = true
    },
    subscriptionCreated({ subscription, priceId, paymentMethodId }) {
      this.subscriptions = {
        data: [subscription],
      }
      iziToast.success({
        title: this.$t('Thank you!'),
        message: this.$t('We are honoured to have you here. You are awesome!'),
        position: 'bottomLeft',
      })
    },
    handleChangeSubscription() {
      iziToast.warning({
        title: 'Warning',
        message:
          this.$t('This feature is not ready yet') +
          '. ' +
          this.$t('Please contact us'),
        position: 'bottomLeft',
      })
    },
    handleCancelSubscription() {
      this.$refs.ConfirmAction.modal({
        title: 'Are you sure?',
        text: 'We will miss you!',
        cta: 'Confirm',
        payload: true,
        action: this.cancelSubscription,
      })
    },
    cancelSubscription() {
      axios
        .post('/account/subscription/cancel', {
          subscriptionId: this.subscriptions.data[0].id,
        })
        .then((response) => {
          iziToast.success({
            title: this.$t('Ok'),
            message:
              this.$t('Your subscription is going to be canceled at') +
              ' ' +
              dayjs.unix(response.data).format('YYYY/MM/DD'),
            position: 'bottomLeft',
          })
        })
        .catch((error) => {
          console.error(error)
        })
    },
    selectPlan(plan) {
      this.selected = plan.priceId
    },
  },
  computed: {
    dayjs(){
      return dayjs
    },
    nextPaymentAttempt() {
      let timestamp = _.get(this.$store.state, 'subscription.last_invoice.next_payment_attempt', null)
      if (timestamp) {
        return dayjs.unix(timestamp).format('YYYY/MM/DD')
      }
      return null
    },
    hasSubscription() {
      let status = _.get(this.subscriptions, 'data[0].status', null)
      if (status) {
        return true
      }
      return false

    },
    hasActiveSubscription() {
      let status = _.get(this.subscriptions, 'data[0].status')
      if (status === 'active') {
        return 1
      }
      return 0
    },
    currentSubscription() {
      return _.get(this.subscriptions, 'data[0]', null)
    },
    currentSubscriptionStatus() {
      return _.get(this.subscriptions, 'data[0].status', null)
    },
    priceId() {
      return this.selected
    },
    selectedPlan() {
      if (this.selected) {
        return Object.values(this.plans).find(
          (plan) => plan.priceId === this.selected,
        )
      }
      return {}
    },
  },
}
</script>
